<template>
  <RouterLink
    :to="karteDetailRouteLocation"
    :class="$style.karte"
    :exact-active-class="$style['karte--active']"
  >
    <div :class="$style.karte__main">
      <ul :class="[$style.karte__attention, $style.attention]">
        <li :class="$style.attention__item">
          <KarteMatchingPotentialText v-if="props.karte.isLikelyToMatch" />
        </li>
        <li :class="$style.attention__item">
          <KartePlannerCommentText
            :sameBranchPlannerCommented="
              props.karte.hasCommentsFromPlannersBelongingToSameBranch
            "
            :plannerCommentCount="props.karte.numberOfPlannerComments"
            :userNumberOfMatch="props.karte.user.numberOfMatch"
          />
        </li>
      </ul>

      <KarteUserProfile
        :class="$style.karte__profile"
        :user="props.karte.user"
      />

      <section :class="[$style.karte__section, $style.section]">
        <h3 :class="$style.heading">
          <IconPerson :class="$style.heading__icon" />ユーザーデータ
        </h3>
        <KarteUserDetailedProfile
          :class="[$style.section__body, $style.details]"
          :diagnosis="props.karte.diagnosis"
        />
      </section>

      <section :class="[$style.karte__section, $style.section]">
        <h3 :class="$style.heading">
          <IconPenLine :class="$style.heading__icon" />
          気になるトピック
        </h3>
        <p
          :class="[
            $style.section__body,
            $style.body,
            {
              [$style['body--empty']]: props.karte.topics?.length === 0,
            },
          ]"
        >
          {{ topic }}
        </p>
      </section>

      <section :class="[$style.karte__section, $style.section]">
        <h3 :class="$style.heading">
          <IconMessage :class="$style.heading__icon" />
          今悩んでいること
        </h3>
        <p
          :class="[
            $style.section__body,
            $style.body,
            {
              [$style['body--empty']]: props.karte.userComment.length === 0,
            },
          ]"
        >
          {{ comment }}
        </p>
      </section>
    </div>
    <footer :class="[$style.karte__footer, $style.footer]">
      <KartePlannerCommentStatusView
        :class="$style.header__status"
        :hasPlannerCommented="!!props.karte.ownKartePlannerComment"
      />

      <p :class="$style.footer__time">
        <time :datetime="modifiedAtAttribute">{{ modifiedAtText }}</time>
      </p>
    </footer>
  </RouterLink>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import { formatDateTime } from '@/libs/utilities/formatDate'
import { formatISO } from 'date-fns'
import KartePlannerCommentStatusView from '../KartePlannerCommentStatusView.vue'
import KartePlannerCommentText from '../KartePlannerCommentText.vue'
import KarteUserDetailedProfile from './KarteUserDetailedProfile.vue'
import KarteMatchingPotentialText from './KarteMatchingPotentialText.vue'
import KarteUserProfile from './KarteUserProfile.vue'

import IconMessage from '@/components/icons/IconMessage.vue'
import IconPenLine from '@/components/icons/IconPenLine.vue'
import IconPerson from '@/components/icons/IconPerson.vue'

import type { RouteLocationRaw } from 'vue-router'
import { type KarteUserListItemKarteFragment } from './KarteUserListItem.generated'
import { fromTimeString } from '@/libs/utilities/gql-scalar'

const props = withDefaults(
  defineProps<{
    karte: KarteUserListItemKarteFragment
    active?: false
  }>(),
  { active: false }
)

const route = useRoute('karteList')

const karteDetailRouteLocation = computed<RouteLocationRaw>(() => {
  return {
    name: 'karteDetail' as const,
    params: {
      entranceGUID: route.params.entranceGUID,
      karteId: props.karte.id,
    },
    query: { ...route.query },
  }
})
const modifiedAt = computed(() => fromTimeString(props.karte.modifiedAt))
const modifiedAtText = computed(() => formatDateTime(modifiedAt.value))
const modifiedAtAttribute = computed(() => formatISO(modifiedAt.value))

const topic = computed((): string => {
  if (props.karte.topics.length === 0) {
    return '未回答'
  }
  return props.karte.topics.map(({ name }) => name).join(', ')
})

const comment = computed((): string => {
  if (!props.karte.userComment) {
    return '未回答'
  }
  return props.karte.userComment
})
</script>

<style lang="scss" module>
.karte {
  display: flex;
  height: 100%;
  flex-direction: column;
  padding: 16px;
  border-left: solid 4px transparent;
  border-radius: $border-radius-v2-rounder;
  color: $color-text-and-object-high-emphasis;
  transition: background-color $transition-interaction;

  &:hover {
    background-color: $color-surface-accent-primary-light;
    text-decoration: none;
  }

  &:active {
    color: $color-text-and-object-high-emphasis;
  }

  &__main {
    flex: 1;
  }

  &__profile {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__section {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.attention {
  &__item {
    font-size: 12px;
    line-height: 20px;
  }
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:nth-child(n + 2) {
    padding-top: 12px;
    border-top: 1px solid $color-border-low-emphasis;
    margin-top: 12px;
  }

  &__time {
    color: $color-text-and-object-medium-emphasis;
    font-size: 14px;
    line-height: 20px;
  }
}

.details {
  padding: 8px;
  background-color: $color-surface-secondary;
  border-radius: 8px;
}

.heading {
  display: flex;
  align-items: center;
  color: $color-text-and-object-high-emphasis;
  font-size: 12px;
  gap: 4px;

  &__icon {
    color: $color-text-and-object-accent-primary;
    font-size: 20px;
  }
}

.section {
  &__body {
    &:nth-child(n + 2) {
      margin-top: 4px;
    }
  }
}

.body {
  color: $color-text-and-object-high-emphasis;
  font-size: 12px;

  &--empty {
    color: $color-text-and-object-low-emphasis;
  }
}
</style>
